/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */
import React from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { adaptV4Theme, Box, IconButton, Theme, ThemeProvider, Typography } from '@mui/material';
import { createTheme, StyledEngineProvider } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import ScopedCssBaseline from '@mui/material/ScopedCssBaseline';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';
import PlatformList from '../../fleet/PlatformList';
import { PlatformListGroup } from '../../../shared/entities/platformListGroup';
import {
  collapseTerminalInBeamListItem,
  expandTerminalInBeamListItem,
  hideTerminalsInYourBeam,
  hideTerminalsInYourBeamPanel,
  openPlatformListDrawer,
  setFtvTerminalsInYourBeamCollapsedTerminals,
  setTerminalsInYourBeamListSortField,
  setTerminalsYourBeamListSortDirection,
  showAllTerminalsInYourBeam,
  showTerminalInYourBeam,
} from '../../../store/ui/actions';
import { FTV_MAP_ID, setMapZoomLatLng } from '../../../store/maps/actions';
import Terminal from '../../../shared/entities/terminal';
import {
  selectFtvTerminalsInYourBeamCollapsedTerminals,
  selectHiddenTerminalsInYourBeamDatapathUuids,
  selectIsInitialTerminalsRequestFinished,
  selectIsRequestingTailDetailsPanelTerminal,
  selectMapSettingsFtvMapHeight,
  selectPlatformListDrawerOpen,
  selectSelectedDatapathUuidInPlatformList,
  selectSelectedTerminalTabIndex,
  selectTerminals,
  selectTerminalsAsArray,
  selectTerminalsInBeam,
  selectTerminalsInYourBeamListItems,
  selectTerminalsInYourBeamPanelShown,
  selectVisibleTerminalsInYourBeamListSortDirection,
  selectVisibleTerminalsInYourBeamListSortField,
} from '../../../store';
import { SortDirection } from '../../../store/ui/reducer';
import { KU_PLUS_KA_DRAWER_WIDTH_PX } from '../../fleet/FleetMap';
import { panToTerminalOnMap } from '../../../utils/MapUtils';

const TOOLBAR_HEIGHT_PX = 99;
export const TERMINALS_IN_YOUR_BEAM_PANEL_WIDTH = 487;

const baseTheme = createTheme(
  adaptV4Theme({
    typography: {
      fontFamily: `'Source Sans Pro', sans-serif`,
    },
    palette: {
      primary: { main: '#5EA215' },
      secondary: { main: '#61DED8' },
      background: {
        paper: '#202E39',
        default: '#142431',
      },
      text: { primary: '#ffffff' },
      action: { hover: '#0D1418', selected: '#000000' },
      divider: '#465967',
    },
  }),
);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    panel: { backgroundColor: '#202E39' },
  }),
);

export const TerminalsInYourBeamPanelContainer = () => {
  // Redux Actions
  const dispatch = useDispatch();

  // Theme and Style
  const classes = useStyles();

  // Redux Selecters
  const platformListDrawerOpen = useSelector(selectPlatformListDrawerOpen, shallowEqual);
  const terminalsInYourBeamPanelShown = useSelector(selectTerminalsInYourBeamPanelShown, shallowEqual);
  const hiddenTerminalsInYourBeamDatapathUuids: string[] = useSelector(
    selectHiddenTerminalsInYourBeamDatapathUuids,
    shallowEqual,
  );
  const selectedDatapathUuidFleetView = useSelector(selectSelectedDatapathUuidInPlatformList, shallowEqual);
  const selectedTerminalTabIndex = useSelector(selectSelectedTerminalTabIndex, shallowEqual);
  const terminalsInYourBeamListSortDirection = useSelector(
    selectVisibleTerminalsInYourBeamListSortDirection,
    shallowEqual,
  );
  const visibleTerminalsInYourBeamListSortField = useSelector(
    selectVisibleTerminalsInYourBeamListSortField,
    shallowEqual,
  );
  const terminals = useSelector(selectTerminalsAsArray, shallowEqual);
  const terminalMap = useSelector(selectTerminals, shallowEqual);
  const isInitialTerminalsRequestFinished = useSelector(selectIsInitialTerminalsRequestFinished, shallowEqual);
  const terminalsInYourBeamListItems = useSelector(selectTerminalsInYourBeamListItems, shallowEqual);
  const isRequestingTailDetailsPanelTerminal = useSelector(selectIsRequestingTailDetailsPanelTerminal, shallowEqual);
  const terminalsInBeamCount = useSelector(selectTerminalsInBeam, shallowEqual).length;

  const mapSettingsFtvMapHeight = useSelector(selectMapSettingsFtvMapHeight(FTV_MAP_ID), shallowEqual);
  const ftvTerminalsInYourBeamCollapsedTerminals = useSelector(
    selectFtvTerminalsInYourBeamCollapsedTerminals,
    shallowEqual,
  );

  return terminalsInYourBeamPanelShown ? (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={baseTheme}>
        <ScopedCssBaseline>
          <Box
            className={classes.panel}
            position="absolute"
            right={0}
            top={0}
            height={Math.floor(mapSettingsFtvMapHeight) + 2}
            zIndex={0}
            borderBottom="1px solid black"
            width={`${KU_PLUS_KA_DRAWER_WIDTH_PX}px`}
          >
            {isInitialTerminalsRequestFinished && !isRequestingTailDetailsPanelTerminal ? (
              <>
                <Box height="48px" width={KU_PLUS_KA_DRAWER_WIDTH_PX} position="relative">
                  <Box position="absolute" flexGrow={1} left="16px" top="24px">
                    <Typography component="div" variant={'body1'}>
                      <Box
                        fontWeight={300}
                        fontSize="20px"
                        lineHeight="20px"
                        data-test="terminals_in_your_beam_button_panel_header"
                      >
                        {`Terminals in my beam`}
                        <span data-test="number_of_terminals_in_beam_header_count">{` (${terminalsInBeamCount})`}</span>
                      </Box>
                    </Typography>
                  </Box>
                  <Box position="absolute" top="8px" right="8px">
                    <IconButton
                      color={'inherit'}
                      data-test="terminals_in_your_beam_panel__dismiss_button"
                      onClick={() => {
                        dispatch(hideTerminalsInYourBeamPanel());
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
                <PlatformList
                  listHeightPx={mapSettingsFtvMapHeight - TOOLBAR_HEIGHT_PX}
                  terminalListItems={{ [PlatformListGroup.TERMINAL]: terminalsInYourBeamListItems }}
                  hiddenDatapathUuids={hiddenTerminalsInYourBeamDatapathUuids}
                  collapsedDatapathUuids={ftvTerminalsInYourBeamCollapsedTerminals}
                  selectedDatapathUuid={selectedDatapathUuidFleetView}
                  selectedTerminalTabIndex={selectedTerminalTabIndex}
                  terminalListSortDirection={terminalsInYourBeamListSortDirection}
                  terminalListSortField={visibleTerminalsInYourBeamListSortField}
                  terminalMap={terminalMap}
                  terminals={{ [PlatformListGroup.TERMINAL]: terminals }}
                  hideTtho={false}
                  /* 
                  Click Handlers 
                */
                  onCollapseGroupClickedHandler={(event: React.MouseEvent, groupName: string) => {
                    event.stopPropagation();
                    // Only one group, PlatformListGroup.TERMINALS, is used. Can ignore groupName
                    dispatch(
                      setFtvTerminalsInYourBeamCollapsedTerminals(
                        terminals.reduce(
                          (prev: any, terminal: Terminal) => ({ ...prev, [terminal.datapathUuid]: true }),
                          {},
                        ),
                      ),
                    );
                  }}
                  onExpandGroupClickedHandler={(event: React.MouseEvent, groupName: string) => {
                    event.stopPropagation();
                    // Only one group, PlatformListGroup.TERMINALS, is used. Can ignore groupName
                    dispatch(setFtvTerminalsInYourBeamCollapsedTerminals({}));
                  }}
                  onHideGroupClickedHandler={(event: React.MouseEvent, groupName: string): void => {
                    event.stopPropagation();
                    // Only one group, PlatformListGroup.TERMINALS, is used. Can ignore groupName
                    dispatch(hideTerminalsInYourBeam(Object.keys(terminalMap)));
                  }}
                  onShowGroupClickedHandler={(event: React.MouseEvent, groupName: string): void => {
                    event.stopPropagation();
                    // Only one group, PlatformListGroup.TERMINALS, is used. Can ignore groupName
                    dispatch(showAllTerminalsInYourBeam());
                  }}
                  onRowClickedHandler={(datapathUuid: string): void => {
                    setTimeout(() => {
                      panToTerminalOnMap(
                        datapathUuid,
                        terminalMap,
                        (latLngObj: google.maps.LatLngLiteral) => {
                          dispatch(setMapZoomLatLng(FTV_MAP_ID)(latLngObj));
                        },
                        hiddenTerminalsInYourBeamDatapathUuids,
                      );
                    });
                  }}
                  onRowCollapseClickedHandler={(datapathUuid: string): void => {
                    const datapathUuidsInGroup = [
                      datapathUuid,
                      ...((terminalMap[datapathUuid] || {}).datapathUuidsOnAircraft || []),
                    ];
                    if (
                      datapathUuidsInGroup.find(
                        (datapathUuidInGroup: string) => ftvTerminalsInYourBeamCollapsedTerminals[datapathUuidInGroup],
                      )
                    ) {
                      datapathUuidsInGroup.forEach((datapathUuidInGroup: string) => {
                        dispatch(expandTerminalInBeamListItem(datapathUuidInGroup));
                      });
                    } else {
                      datapathUuidsInGroup.forEach((datapathUuidInGroup: string) => {
                        dispatch(collapseTerminalInBeamListItem(datapathUuidInGroup));
                      });
                    }
                  }}
                  onRowHideClickedHandler={(datapathUuid: string): void => {
                    const datapathUuids = [datapathUuid, ...((terminalMap[datapathUuid] || {}).datapathUuidsOnAircraft || [])];
                    if (hiddenTerminalsInYourBeamDatapathUuids.includes(datapathUuid)) {
                      datapathUuids.forEach((datapathUuidInBeam: string) => {
                        dispatch(showTerminalInYourBeam(datapathUuidInBeam));
                      });
                    } else {
                      dispatch(hideTerminalsInYourBeam(datapathUuids));
                    }
                  }}
                  onSortColumnHeaderClickedHandler={(
                    sortField: string,
                    sortDirection: SortDirection,
                    groupName: string,
                  ): void => {
                    dispatch(setTerminalsInYourBeamListSortField(sortField));

                    // Can ignore groupName. TerminalsInYourBeamPanel has its own redux values for sort direction that are independent from group
                    dispatch(setTerminalsYourBeamListSortDirection(sortDirection));
                  }}
                  onRowSubItemClickedHandler={(datapathUuid: string): void => {
                    setTimeout(() => {
                      panToTerminalOnMap(
                        datapathUuid,
                        terminalMap,
                        (latLngObj: google.maps.LatLngLiteral) => {
                          dispatch(setMapZoomLatLng(FTV_MAP_ID)(latLngObj));
                        },
                        hiddenTerminalsInYourBeamDatapathUuids,
                      );
                    });
                  }}
                  openListDrawer={openPlatformListDrawer}
                  listDrawerOpen={platformListDrawerOpen}
                />
              </>
            ) : (
              <Box textAlign="center" paddingTop="75px">
                <div className="loading__error__container">
                  <div className="loading__error__message__container">
                    <CircularProgress className="progress" />
                  </div>
                </div>
              </Box>
            )}
          </Box>
        </ScopedCssBaseline>
      </ThemeProvider>
    </StyledEngineProvider>
  ) : (
    <div />
  );
};
